import { useQuery } from "@tanstack/react-query";
import type {
  SuperAdminPaymentEntry,
  SuperAdminPaymentStatut,
} from "@victor/types";
import { api } from "../../../lib/api";
import { PageSpinner } from "../../../ui/Spinner";
import {
  Badge,
  EmptyInline,
  Panel,
  SectionTitle,
  fmtDateTime,
  fmtMoney,
} from "./_ui";

type Props = { tenantId: string };

const STATUT_TONE: Record<
  SuperAdminPaymentStatut,
  Parameters<typeof Badge>[0]["tone"]
> = {
  paid: "emerald",
  succeeded: "emerald",
  open: "amber",
  draft: "slate",
  void: "slate",
  uncollectible: "red",
  failed: "red",
};

export function PaymentsTab({ tenantId }: Props) {
  const paymentsQ = useQuery({
    queryKey: ["tenant-payments", tenantId],
    queryFn: () => api.payments.list(tenantId),
  });

  if (paymentsQ.isLoading) return <PageSpinner />;

  const rows = paymentsQ.data ?? [];

  return (
    <div className="space-y-5">
      <Panel accent="info">
        <SectionTitle icon={ICON.card} desc="Factures Stripe (abonnement + packs) + transactions crédits (recharges manuelles, offerts, consommations).">
          Paiements vers MyVictor
        </SectionTitle>
      </Panel>

      <Panel padded={false}>
        {rows.length === 0 ? (
          <div className="p-5">
            <EmptyInline>Aucun paiement enregistré.</EmptyInline>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-navy-100">
                <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                  Date
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                  Source
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                  Libellé
                </th>
                <th className="px-5 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-navy-500">
                  Montant
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                  Statut
                </th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-100">
              {rows.map((r, i) => (
                <PaymentRow key={`${r.source}-${r.ref}-${i}`} entry={r} />
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}

function PaymentRow({ entry: r }: { entry: SuperAdminPaymentEntry }) {
  return (
    <tr className="transition-colors hover:bg-navy-50/40">
      <td className="px-5 py-3 whitespace-nowrap text-xs text-navy-500">
        {fmtDateTime(r.date)}
      </td>
      <td className="px-5 py-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-bold uppercase tracking-wider text-navy-500">
            {r.source}
          </span>
          {r.paymentMethodType ? (
            <span className="inline-flex w-fit items-center rounded-full bg-navy-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-navy-700">
              {labelPm(r.paymentMethodType)}
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-5 py-3">
        <p className="font-semibold text-navy-800">{r.libelle}</p>
        <p className="mt-0.5 text-[11px] text-navy-400">{r.ref}</p>
      </td>
      <td className="px-5 py-3 text-right tabular-nums whitespace-nowrap">
        {r.montant != null ? (
          <span className="font-semibold text-navy-800">
            {fmtMoney(r.montant)} €
          </span>
        ) : r.quantite != null ? (
          <span
            className={`font-semibold ${r.quantite > 0 ? "text-emerald-700" : "text-navy-500"}`}
          >
            {r.quantite > 0 ? "+" : ""}
            {r.quantite}
          </span>
        ) : (
          <span className="text-navy-400">-</span>
        )}
      </td>
      <td className="px-5 py-3">
        <Badge tone={STATUT_TONE[r.statut] ?? "slate"} dot>
          {r.statut}
        </Badge>
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        {r.hostedInvoiceUrl ? (
          <a
            href={r.hostedInvoiceUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-bold text-accent-500 hover:underline"
          >
            Voir →
          </a>
        ) : r.invoicePdfUrl ? (
          <a
            href={r.invoicePdfUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-bold text-accent-500 hover:underline"
          >
            PDF
          </a>
        ) : null}
      </td>
    </tr>
  );
}

function labelPm(t: string): string {
  if (t === "sepa_debit") return "SEPA";
  if (t === "card") return "CB";
  if (t === "sofort") return "Sofort";
  if (t === "paypal") return "PayPal";
  return t;
}

const sp = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  viewBox: "0 0 24 24",
};
const ICON = {
  card: (
    <svg {...sp}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </svg>
  ),
};
